// AxisCare → CAERA one-off import (Settings → AxisCare Import).
//
// The browser pulls AxisCare's raw visits month by month and its schedule
// patterns through the existing proxy, then hands each batch to the two
// import RPCs, which do all mapping and writing server-side. Re-running is
// safe: visits upsert on AxisCare's visit id, schedules on their pattern key.
import { supabase } from './supabase';
import { getVisits, getSchedules } from './axiscare';

// AxisCare answers an empty window with 404 "No visits found" rather than an
// empty list; the proxy relays that status, so read the body before deciding.
const unpack = async (res) => {
  if (res.error) {
    const body = await res.error.context?.json?.().catch(() => null);
    const errs = body?.errors || [];
    if (errs.some(e => /no (visits|schedules) found/i.test(String(e)))) return [];
    throw new Error(errs.length ? errs.join('; ') : (res.error.message || String(res.error)));
  }
  if (res.data?.errors?.length) throw new Error(res.data.errors.join('; '));
  const raw = res.data?.results?.visits ?? res.data?.results?.schedules;
  return Array.isArray(raw) ? raw : Object.entries(raw || {}).map(([id, v]) => ({ ...v, id: v.id ?? id }));
};

// AxisCare renamed its service codes mid-2026; older visits carry the old
// names. Each catalog line lists the old names as aliases, and rows are
// rewritten to the current code before the database sees them.
const loadAliasMap = async () => {
  const { data, error } = await supabase.from('service_catalog').select('axiscare_service_code, axiscare_service_aliases');
  if (error) throw new Error(error.message);
  const map = {};
  (data || []).forEach(l => (l.axiscare_service_aliases || []).forEach(a => { if (l.axiscare_service_code) map[a] = l.axiscare_service_code; }));
  return map;
};
const applyAliases = (rows, map) =>
  rows.map(r => (r.service?.code && map[r.service.code]) ? { ...r, service: { ...r.service, code: map[r.service.code] } } : r);

export const fetchAxisCareVisits = async (startDate, endDate) =>
  applyAliases(await unpack(await getVisits({ startDate, endDate })), await loadAliasMap());
// AxisCare needs a date window; patterns active in the window come back one row per weekday.
export const fetchAxisCareSchedules = async (startDate, endDate) =>
  applyAliases(await unpack(await getSchedules({ startDate, endDate })), await loadAliasMap());

export const importVisits = async (rows, { dryRun = true, linkByName = true } = {}) => {
  const { data, error } = await supabase.rpc('import_axiscare_visits', { p_rows: rows, p_dry_run: dryRun, p_link_by_name: linkByName });
  if (error) throw new Error(error.message);
  return data;
};
export const importSchedules = async (rows, { dryRun = true, linkByName = true } = {}) => {
  const { data, error } = await supabase.rpc('import_axiscare_schedules', { p_rows: rows, p_dry_run: dryRun, p_link_by_name: linkByName });
  if (error) throw new Error(error.message);
  return data;
};
export const undoImport = async () => {
  const { data, error } = await supabase.rpc('undo_axiscare_import');
  if (error) throw new Error(error.message);
  return data;
};
export const getImportRuns = () =>
  supabase.from('import_runs').select('id, kind, range_start, range_end, dry_run, result, run_at, run_by:profiles ( full_name )')
    .order('run_at', { ascending: false }).limit(25);
export const getImportedCounts = async () => {
  const [v, s] = await Promise.all([
    supabase.from('visits').select('id', { count: 'exact', head: true }).eq('source', 'axiscare'),
    supabase.from('schedules').select('id', { count: 'exact', head: true }).eq('source', 'axiscare'),
  ]);
  return { visits: v.count || 0, schedules: s.count || 0 };
};

export const isoDate = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};
export const addDays = (iso, n) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + n); return isoDate(d); };

// [from, to] split at month ends so no single AxisCare call exceeds the
// proxy's page cap (25 pages × 100 visits).
export const monthChunks = (from, to) => {
  const out = [];
  let cur = from;
  while (cur <= to) {
    const d = new Date(`${cur}T12:00:00`);
    const monthEnd = isoDate(new Date(d.getFullYear(), d.getMonth() + 1, 0));
    const end = monthEnd < to ? monthEnd : to;
    out.push([cur, end]);
    cur = addDays(end, 1);
  }
  return out;
};

// Merge per-month results into one summary for the page.
export const mergeVisitResults = (acc, r) => {
  const next = acc || { dry_run: !!r.dry_run, rows: 0, inserted: 0, updated: 0, kept_edited_in_caera: 0, skipped_unmatched: 0, linked_by_name: { clients: 0, caregivers: 0 }, by_status: {}, unmatched: { clients: {}, caregivers: {}, services: {} } };
  next.rows += r.rows || 0; next.inserted += r.inserted || 0; next.updated += r.updated || 0;
  next.kept_edited_in_caera += r.kept_edited_in_caera || 0; next.skipped_unmatched += r.skipped_unmatched || 0;
  next.linked_by_name.clients = Math.max(next.linked_by_name.clients, r.linked_by_name?.clients || 0);
  next.linked_by_name.caregivers = Math.max(next.linked_by_name.caregivers, r.linked_by_name?.caregivers || 0);
  Object.entries(r.by_status || {}).forEach(([k, n]) => { next.by_status[k] = (next.by_status[k] || 0) + n; });
  ['clients', 'caregivers', 'services'].forEach(kind => {
    (r.unmatched?.[kind] || []).forEach(u => { next.unmatched[kind][`${u.axiscare_id ?? u.code}|${u.name}`] = u; });
  });
  return next;
};
